import { AxiosResponse } from 'axios';
import heic2any from 'heic2any';

import { DEFAULT_THUMBNAIL_EXTENSION, STOCK_CLIP } from '@/constants';
import { AssetScope, AssetType, FilePickerStatus } from '@/enums';
import { BlobLocation } from '@/enums/BlobLocationEnum';
import {
  checkIfAssetNeedsThumbnail,
  getAssetBlobClient,
  getAssetExtension,
  getAssetType,
  getAssetUrl,
  getThumbnailFromType,
  stripAudioCoverArt,
  uploadAsset,
  validateAssetForUpload
} from '@/helpers';
import {
  AssetUploadErrorType,
  CreateEditAssetPayload,
  FilePickerResult,
  FilePickerType,
  SasTokenPayload,
  ThumbnailUploadErrorResponse,
  ThumbnailUploadPayload
} from '@/types';

type FilePickerWindow = Window & {
  showOpenFilePicker?: (options: {
    multiple?: boolean;
    excludeAcceptAllOption?: boolean;
    types: FilePickerType[];
  }) => Promise<Array<{ getFile: () => Promise<File> }>>;
};

type CreateSasTokenFn = (
  payload: SasTokenPayload
) => Promise<AxiosResponse<ThumbnailUploadPayload, ThumbnailUploadErrorResponse> | undefined>;

type UploadContext = {
  eventId?: string | number | null;
  organizationId?: string | number | null;
  themeId?: number | null;
  isPublic?: boolean;
  assetScope?: AssetScope;
  blobLocation?: BlobLocation;
};

type ValidateUploadFileResult = {
  assetType?: AssetType;
  errorType?: AssetUploadErrorType;
};

export const openUploadPicker = async ({
  pickerTypes,
  multiple = true
}: {
  pickerTypes: FilePickerType[];
  multiple?: boolean;
}): Promise<FilePickerResult> => {
  const pickerWindow = window as FilePickerWindow;

  if (!pickerWindow.showOpenFilePicker) {
    return FilePickerStatus.UNSUPPORTED;
  }

  try {
    const handles = await pickerWindow.showOpenFilePicker({
      multiple,
      excludeAcceptAllOption: true,
      types: pickerTypes
    });

    return Promise.all(handles.map((handle) => handle.getFile()));
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return FilePickerStatus.CANCELLED;
    }
    return FilePickerStatus.UNSUPPORTED;
  }
};

const getPickerFormats = (pickerTypes: FilePickerType[]): string[] => {
  return Array.from(
    new Set(
      pickerTypes.flatMap(({ accept }) =>
        Object.entries(accept).flatMap(([mimeType, extensions]) => [mimeType, ...extensions])
      )
    )
  );
};

export const getInputAcceptFromPickerTypes = (pickerTypes: FilePickerType[]): string => {
  return getPickerFormats(pickerTypes).join(',');
};

export const getValidationFormatsFromPickerTypes = (pickerTypes: FilePickerType[]): string[] => {
  const formats = getPickerFormats(pickerTypes);

  return Array.from(
    new Set([
      ...formats,
      ...formats
        .filter((format) => format.startsWith('.'))
        .map((extension) => extension.replace('.', ''))
    ])
  );
};

export const sortUploadFilesByName = (files: File[] | FileList): File[] => {
  return Array.from(files).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true })
  );
};

export const resolveUploadOwnerId = ({
  eventId,
  organizationId,
  themeId
}: Pick<UploadContext, 'eventId' | 'organizationId' | 'themeId'>): number => {
  if (eventId) {
    return Number(eventId);
  }

  if (organizationId) {
    return Number(organizationId);
  }

  if (themeId) {
    return themeId;
  }

  return 0;
};

export const resolveUploadBlobLocation = ({
  isPublic,
  assetScope,
  blobLocation
}: UploadContext): BlobLocation => {
  if (blobLocation !== undefined) {
    return blobLocation;
  }
  if (isPublic || assetScope === AssetScope.PUBLIC) {
    return BlobLocation.PUBLIC;
  }

  if (assetScope === AssetScope.ORGANIZATION) {
    return BlobLocation.ORGANIZATIONS;
  }

  return BlobLocation.EVENTS;
};

export const resolveUploadAssetScope = ({
  isPublic,
  assetScope
}: Pick<UploadContext, 'isPublic' | 'assetScope'>): AssetScope => {
  if (assetScope) {
    return assetScope;
  }

  return isPublic ? AssetScope.PUBLIC : AssetScope.PRIVATE;
};

export const buildUploadSasPayload = ({
  assetName = '',
  assetScope,
  eventId,
  file,
  isPublic = false,
  isThumbnail = false,
  organizationId,
  themeId,
  blobLocation
}: UploadContext & {
  file: File;
  isThumbnail?: boolean;
  assetName?: string;
}): SasTokenPayload => {
  return {
    id: resolveUploadOwnerId({ eventId, organizationId, themeId }),
    isPublic,
    isThumbnail,
    assetName,
    extension: isThumbnail ? DEFAULT_THUMBNAIL_EXTENSION : getAssetExtension(file.name),
    blobLocation: resolveUploadBlobLocation({
      isPublic,
      assetScope,
      blobLocation
    })
  };
};

const buildUploadTags = (
  assetTag?: string | string[]
): CreateEditAssetPayload['tags'] | undefined => {
  if (!assetTag) {
    return undefined;
  }

  const tagNames = Array.isArray(assetTag) ? assetTag : [assetTag];

  return tagNames.map((name) => ({ name }));
};

export const buildUploadedAssetPayload = ({
  file,
  assetUrl,
  assetType,
  eventId,
  organizationId,
  isPublic = false,
  assetScope,
  assetTag,
  duration
}: UploadContext & {
  file: File;
  assetUrl: string;
  assetType: AssetType;
  assetTag?: string | string[];
  duration?: number;
}): CreateEditAssetPayload => {
  return {
    assetType,
    assetScope: resolveUploadAssetScope({ isPublic, assetScope }),
    assetName: file.name,
    assetUrl,
    assetSize: Number(file.size.toFixed()),
    thumbnailUrl: '',
    organizationId: organizationId ? Number(organizationId) : undefined,
    duration,
    ...(assetTag && {
      tags: buildUploadTags(assetTag)
    }),
    ...(!isPublic && {
      eventId: eventId ? Number(eventId) : null
    })
  } as CreateEditAssetPayload;
};

export const getUploadMediaDuration = async (
  file: File,
  assetType: AssetType
): Promise<number | undefined> => {
  if (
    assetType !== AssetType.AUDIO &&
    assetType !== AssetType.VIDEO &&
    assetType !== AssetType.STOCK_CLIPS
  ) {
    return undefined;
  }

  return new Promise<number>((resolve) => {
    const mediaElement =
      assetType === AssetType.AUDIO
        ? document.createElement('audio')
        : document.createElement('video');

    const mediaUrl = URL.createObjectURL(file);

    const cleanup = () => {
      mediaElement.onloadedmetadata = null;
      mediaElement.onerror = null;
      mediaElement.removeAttribute('src');
      mediaElement.load();
      URL.revokeObjectURL(mediaUrl);
    };

    mediaElement.preload = 'metadata';

    mediaElement.onloadedmetadata = () => {
      const duration = Math.floor(mediaElement.duration);

      cleanup();

      resolve(Number.isFinite(duration) && duration > 0 ? duration : 0);
    };

    mediaElement.onerror = () => {
      cleanup();
      resolve(0);
    };

    mediaElement.src = mediaUrl;
  });
};

export const validateUploadFile = ({
  file,
  validationFormats,
  uploadAssetType
}: {
  file: File;
  validationFormats: string[];
  uploadAssetType?: AssetType | string;
}): ValidateUploadFileResult => {
  const validationError = validateAssetForUpload(file, validationFormats);

  if (validationError) {
    return { errorType: validationError };
  }

  const detectedAssetType = getAssetType(file);

  const mappedUploadAssetType =
    typeof uploadAssetType === 'number'
      ? uploadAssetType
      : isStockClipUploadType(uploadAssetType)
        ? AssetType.STOCK_CLIPS
        : undefined;

  const assetType = mappedUploadAssetType ?? detectedAssetType;

  if (assetType === null || assetType === undefined) {
    return { errorType: AssetUploadErrorType.ASSET_TYPE_NOT_SUPPORTED };
  }

  return { assetType };
};

export const prepareUploadFile = async (
  file: File,
  assetType: AssetType
): Promise<{
  fileToUpload?: File;
  duration?: number;
  errorType?: AssetUploadErrorType;
}> => {
  let fileToUpload = file;

  if (assetType === AssetType.IMAGE) {
    const isHeic = await isHeicFileContent(file);

    if (isHeic) {
      try {
        fileToUpload = await convertHeicToJpeg(file);
      } catch {
        return {
          errorType: AssetUploadErrorType.UNSUPPORTED_MEDIA_FORMAT
        };
      }
    }
  }

  if (assetType === AssetType.AUDIO) {
    const processedFile = await stripAudioCoverArt(file);

    if (!processedFile) {
      return {
        errorType: AssetUploadErrorType.AUDIO_PROCESSING_FAILED
      };
    }

    fileToUpload = processedFile;
  }

  const duration = await getUploadMediaDuration(fileToUpload, assetType);

  return {
    fileToUpload,
    duration
  };
};

export const uploadFileWithSasToken = async ({
  file,
  sasTokenData
}: {
  file: File;
  sasTokenData: AxiosResponse<ThumbnailUploadPayload, ThumbnailUploadErrorResponse>;
}): Promise<string | null> => {
  const blobClient = getAssetBlobClient(sasTokenData);
  const isUploaded = await uploadAsset(blobClient, file);

  if (!isUploaded) {
    return null;
  }

  return getAssetUrl(sasTokenData);
};

export const addThumbnailToAssetPayload = async ({
  assetPayload,
  assetScope,
  assetType,
  createSasToken,
  eventId,
  isPublic,
  organizationId,
  originalFile,
  originalSasAssetName
}: UploadContext & {
  originalFile: File;
  originalSasAssetName: string;
  assetPayload: CreateEditAssetPayload;
  assetType: AssetType;
  createSasToken: CreateSasTokenFn;
}): Promise<CreateEditAssetPayload> => {
  if (!checkIfAssetNeedsThumbnail(assetType)) {
    return assetPayload;
  }

  const thumbnail = await getThumbnailFromType(originalFile);

  if (!thumbnail?.file) {
    return assetPayload;
  }

  const thumbnailSasPayload = buildUploadSasPayload({
    assetName: originalSasAssetName,
    assetScope,
    file: thumbnail.file,
    eventId,
    isPublic,
    isThumbnail: true,
    organizationId
  });

  const thumbnailSasTokenData = await createSasToken(thumbnailSasPayload);

  if (!thumbnailSasTokenData) {
    return assetPayload;
  }

  const thumbnailUrl = await uploadFileWithSasToken({
    file: thumbnail.file,
    sasTokenData: thumbnailSasTokenData
  });

  if (!thumbnailUrl) {
    return assetPayload;
  }

  return {
    ...assetPayload,
    thumbnailUrl
  };
};

export const isStockClipUploadType = (uploadAssetType?: AssetType | string): boolean => {
  return uploadAssetType === STOCK_CLIP;
};
export const getFileSignature = async (file: File): Promise<string> => {
  const buffer = await file.slice(0, 16).arrayBuffer();

  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

export const isHeicFileContent = async (file: File): Promise<boolean> => {
  const buffer = await file.slice(4, 12).arrayBuffer();
  const brand = new TextDecoder().decode(buffer);

  return ['ftypheic', 'ftypheix', 'ftyphevc', 'ftyphevx', 'ftypmif1', 'ftypmsf1'].includes(brand);
};

export const convertHeicToJpeg = async (file: File): Promise<File> => {
  const convertedBlob = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.9
  });

  const jpegBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

  const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');

  return new File([jpegBlob], `${fileNameWithoutExtension}.jpeg`, {
    type: 'image/jpeg',
    lastModified: Date.now()
  });
};
